import { CartItem } from '../types';

export interface ThermalReceiptData {
  saleId: number;
  customerName?: string;
  customerPhone?: string;
  items: CartItem[];
  subtotal: number;
  discount: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  paidAmount: number;
  changeAmount: number;
  dueAmount?: number; // Add due amount for partial payments
  paymentMethod: 'cash' | 'card' | 'upi' | 'credit' | 'bank_transfer' | 'other';
  saleDate: string;
  currencySymbol?: string; // Dynamic currency symbol
  // Print settings
  storeName?: string;
  storePhone?: string;
  storeAddress?: string;
}

export class ThermalPrinter {
  // Standard thermal printer width (characters)
  private static readonly PRINT_WIDTH = 32;
  private static readonly SEPARATOR = '-'.repeat(ThermalPrinter.PRINT_WIDTH);
  private static readonly DOUBLE_SEPARATOR = '='.repeat(ThermalPrinter.PRINT_WIDTH);

  // ESC/POS commands for thermal printers
  private static readonly ESC = '\x1B';
  private static readonly GS = '\x1D';
  
  // Formatting commands
  private static readonly INIT = ThermalPrinter.ESC + '@'; // Initialize printer
  private static readonly BOLD_ON = ThermalPrinter.ESC + 'E' + '\x01'; // Bold on
  private static readonly BOLD_OFF = ThermalPrinter.ESC + 'E' + '\x00'; // Bold off
  private static readonly CENTER = ThermalPrinter.ESC + 'a' + '\x01'; // Center align
  private static readonly LEFT = ThermalPrinter.ESC + 'a' + '\x00'; // Left align
  private static readonly CUT_PAPER = ThermalPrinter.GS + 'V' + '\x42' + '\x00'; // Cut paper
  private static readonly DOUBLE_HEIGHT = ThermalPrinter.ESC + '!' + '\x10'; // Double height
  private static readonly NORMAL_SIZE = ThermalPrinter.ESC + '!' + '\x00'; // Normal size

  static formatReceipt(data: ThermalReceiptData): string {
    const currencySymbol = data.currencySymbol || '₨'; // Fallback to Rs if not provided
    let receipt = '';
    
    // Initialize printer
    receipt += ThermalPrinter.INIT;
    
    // Header
    receipt += ThermalPrinter.CENTER + ThermalPrinter.BOLD_ON + ThermalPrinter.DOUBLE_HEIGHT;
    const storeName = data.storeName || 'NextGen - Shop';
    receipt += storeName + '\n';
    receipt += ThermalPrinter.NORMAL_SIZE + ThermalPrinter.BOLD_OFF;
    receipt += (data.storePhone || '+92 348 119 6365') + '\n';
    const address = data.storeAddress || '123 Main Street, Suite 456, Springfield, USA 12345';
    const addressLines = address.split('\n');
    addressLines.forEach((line: string) => receipt += line.trim() + '\n');
    receipt += ThermalPrinter.LEFT;
    receipt += ThermalPrinter.DOUBLE_SEPARATOR + '\n';
    
    // Sale info
    receipt += `Sale #: ${data.saleId.toString().padStart(6, '0')}\n`;
    receipt += `Date: ${new Date(data.saleDate).toLocaleString('en-GB')}\n`;
    
    if (data.customerName) {
      receipt += `Customer: ${ThermalPrinter.truncate(data.customerName, 20)}\n`;
    }
    if (data.customerPhone) {
      receipt += `Phone: ${data.customerPhone}\n`;
    }
    
    receipt += ThermalPrinter.SEPARATOR + '\n';
    
    // Items header
    receipt += ThermalPrinter.BOLD_ON;
    receipt += 'Item                 Qty  Price\n';
    receipt += ThermalPrinter.BOLD_OFF;
    receipt += ThermalPrinter.SEPARATOR + '\n';
    
    // Items
    data.items.forEach(item => {
      const product = item.product;
      const itemName = ThermalPrinter.truncate(product?.name || '', 18);
      const qty = `${item.quantity}x`;
      const price = `${currencySymbol}${item.total.toFixed(2)}`;
      
      receipt += `${itemName.padEnd(18)} ${qty.padEnd(4)} ${price.padStart(7)}\n`;
      
      // Add brand info if space allows
      if (product?.brand) {
        const brand = ThermalPrinter.truncate(`  ${product.brand}`, 25);
        receipt += `${brand}\n`;
      }
    });
    
    receipt += ThermalPrinter.SEPARATOR + '\n';
    
    // Totals
    receipt += `Subtotal:${(currencySymbol + data.subtotal.toFixed(2)).padStart(24)}\n`;
    
    if (data.discountAmount > 0) {
      receipt += `Discount (${data.discount}%):${('-' + currencySymbol + data.discountAmount.toFixed(2)).padStart(19)}\n`;
    }
    
    if (data.taxAmount > 0) {
      receipt += `Tax (${data.taxRate}%):${(currencySymbol + data.taxAmount.toFixed(2)).padStart(24)}\n`;
    }
    
    receipt += ThermalPrinter.BOLD_ON;
    receipt += ThermalPrinter.DOUBLE_SEPARATOR + '\n';
    receipt += `TOTAL:${(currencySymbol + data.total.toFixed(2)).padStart(26)}\n`;
    receipt += ThermalPrinter.DOUBLE_SEPARATOR + '\n';
    receipt += ThermalPrinter.BOLD_OFF;
    
    // Payment info
    receipt += `Payment (${data.paymentMethod.toUpperCase()}):${(currencySymbol + data.paidAmount.toFixed(2)).padStart(17)}\n`;
    
    // Show change or due amount
    if (data.changeAmount > 0) {
      receipt += `Change:${(currencySymbol + data.changeAmount.toFixed(2)).padStart(25)}\n`;
    } else if (data.dueAmount && data.dueAmount > 0) {
      receipt += ThermalPrinter.BOLD_ON;
      receipt += `Due Amount:${(currencySymbol + data.dueAmount.toFixed(2)).padStart(21)}\n`;
      receipt += ThermalPrinter.BOLD_OFF;
    }
    
    receipt += ThermalPrinter.SEPARATOR + '\n';
    
    // Footer with extra spacing
    receipt += '\n';
    receipt += ThermalPrinter.CENTER;
    receipt += 'Thank you for your visit!\n';
    receipt += 'Powered by HindukushSoft Technologies\n';
    receipt += ThermalPrinter.LEFT;
    
    // Cut paper
    receipt += '\n\n\n';
    receipt += ThermalPrinter.CUT_PAPER;
    
    return receipt;
  }

  // Generate HTML receipt for preview/browser printing
  static generateHtmlReceipt(data: ThermalReceiptData): string {
    const currencySymbol = data.currencySymbol || '₨'; // Fallback to Rs if not provided
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Receipt - Sale #${data.saleId}</title>
    <style>
        @page {
            size: 80mm auto; /* Thermal printer width */
            margin: 0;
        }
        
        body {
            font-family: 'Courier New', 'Lucida Console', monospace;
            font-size: 16px;
            line-height: 1.3;
            margin: 0;
            padding: 20px;
            width: 300px;
            background: white;
            color: #000;
        }
        
        .receipt-page {
            page-break-after: always;
            min-height: 0;
        }
        
        .receipt-page:last-child {
            page-break-after: auto;
        }
        
        .receipt-header {
            page-break-inside: avoid;
        }
        
        .items-section {
            page-break-inside: auto;
        }
        
        .item-data {
            page-break-inside: avoid;
        }
        
        .totals-section {
            page-break-before: auto;
            page-break-inside: avoid;
        }
        
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .large { font-size: 16px; }
        .separator { border-bottom: 1px dashed #000; margin: 5px 0; }
        .double-separator { border-bottom: 2px solid #000; margin: 5px 0; }
        .item-row { 
            display: flex; 
            justify-content: space-between; 
            align-items: center;
        }
        .item-header {
            display: flex;
            font-weight: bold;
        }
        .item-header .name { width: 60%; text-align: left; }
        .item-header .qty { width: 20%; text-align: center; }
        .item-header .price { width: 20%; text-align: right; }
        .item-data {
            display: flex;
            align-items: center;
            margin: 2px 0;
        }
        .item-data .name { width: 60%; text-align: left; }
        .item-data .qty { width: 20%; text-align: center; }
        .item-data .price { width: 20%; text-align: right; }
        .total-row { display: flex; justify-content: space-between; font-weight: bold; }
        
        @media print {
            body { 
                margin: 0; 
                padding: 10px;
                width: 80mm;
                font-size: 16px;
                font-weight: 500;
            }
            .no-print { display: none; }
            .receipt-page {
                page-break-after: always;
            }
            .receipt-page:last-child {
                page-break-after: auto;
            }
            .center { font-size: 16px; }
            .bold { font-weight: bold; }
            .large { font-size: 18px; font-weight: bold; }
        }
    </style>
</head>
<body>
    <div class="receipt-header">
        <div class="center">
            <div class="bold large">${data.storeName || 'NextGen - Shop'}</div>
            <div>${data.storePhone || '+92 348 119 6365'}</div>
            ${(data.storeAddress || '123 Main Street, Suite 456, Springfield, USA 12345').split('\n').map((line: string) => `<div>${line.trim()}</div>`).join('')}
        </div>
        <div class="double-separator"></div>
        
        <div>Sale #: ${data.saleId.toString().padStart(6, '0')}</div>
        <div>Date: ${new Date(data.saleDate).toLocaleString('en-GB')}</div>
        ${data.customerName ? `<div>Customer: ${data.customerName}</div>` : ''}
        ${data.customerPhone ? `<div>Phone: ${data.customerPhone}</div>` : ''}
        
        <div class="separator"></div>
        
        <div class="item-header">
            <span class="name">Item</span>
            <span class="qty">Qty</span>
            <span class="price">Price</span>
        </div>
        <div class="separator"></div>
    </div>
    
    <div class="items-section">
        ${data.items.map(item => {
            const product = item.product;
            return `
        <div class="item-data">
            <span class="name">${ThermalPrinter.truncate(product?.name || '', 18)}</span>
            <span class="qty">${item.quantity}x</span>
            <span class="price">${currencySymbol}${item.total.toFixed(2)}</span>
        </div>
        ${product?.brand ? `<div style="font-size: 10px; color: #666; margin-left: 10px;">${product.brand}</div>` : ''}
    `}).join('')}
    </div>
    </div>
    
    <div class="totals-section">
        <div class="separator"></div>
        
        <div class="item-row"><span>Subtotal:</span><span>${currencySymbol}${data.subtotal.toFixed(2)}</span></div>
        ${data.discountAmount > 0 ? `<div class="item-row"><span>Discount (${data.discount}%):</span><span>-${currencySymbol}${data.discountAmount.toFixed(2)}</span></div>` : ''}
        ${data.taxAmount > 0 ? `<div class="item-row"><span>Tax (${data.taxRate}%):</span><span>${currencySymbol}${data.taxAmount.toFixed(2)}</span></div>` : ''}
        
        <div class="double-separator"></div>
        <div class="total-row"><span>TOTAL:</span><span>${currencySymbol}${data.total.toFixed(2)}</span></div>
        <div class="double-separator"></div>
        
        <div class="item-row"><span>Payment (${data.paymentMethod.toUpperCase()}):</span><span>${currencySymbol}${data.paidAmount.toFixed(2)}</span></div>
        ${data.changeAmount > 0 ? `<div class="item-row"><span>Change:</span><span>${currencySymbol}${data.changeAmount.toFixed(2)}</span></div>` : ''}
        ${data.dueAmount && data.dueAmount > 0 ? `<div class="item-row" style="font-weight: bold; color: #dc2626;"><span>Due Amount:</span><span>${currencySymbol}${data.dueAmount.toFixed(2)}</span></div>` : ''}
        
        <div class="separator"></div>
        
        <div class="center" style="margin-top: 15px;">
            <div>Thank you for your visit!</div>
            <div>Powered by HindukushSoft Technologies</div>
        </div>
    </div>
    
    <div class="no-print" style="margin-top: 20px; text-align: center;">
        <button onclick="window.print()">Print Receipt</button>
        <button onclick="window.close()">Close</button>
    </div>
</body>
</html>`;
  }

  // Send to thermal printer (requires printer driver/service)
  static async printToThermalPrinter(data: ThermalReceiptData): Promise<boolean> {
    try {
      const receiptText = ThermalPrinter.formatReceipt(data);
      
      // Method 1: Try to use Web Serial API (modern browsers)
      if ('serial' in navigator) {
        // This would require user to grant permission to serial port
        // Implementation depends on specific thermal printer model
        console.log('Web Serial API available - would connect to thermal printer');
      }
      
      // Method 2: Use print service endpoint (if available)
      const response = await fetch('/api/print/thermal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ receiptData: receiptText })
      });
      
      if (response.ok) {
        return true;
      }
      
      // Method 3: Fallback to browser print
      ThermalPrinter.printHtmlReceipt(data);
      return true;
      
    } catch (error) {
      console.error('Failed to print to thermal printer:', error);
      // Fallback to HTML print
      ThermalPrinter.printHtmlReceipt(data);
      return false;
    }
  }

  // Print HTML receipt in new window
  static printHtmlReceipt(data: ThermalReceiptData): void {
    const htmlContent = ThermalPrinter.generateHtmlReceipt(data);
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Auto print after loading
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }

  private static truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength - 3) + '...';
  }
}